<template>
  <div class="">
    <div class="flex flex-col md:flex-row md:items-center md:justify-end mb-3 gap-2">
      <vs-select v-model="language" @change="fetchAllApps(language)" class="w-28" :color="'#6366f1'">
        <vs-select-item value="all" text="All Languages" />
        <vs-select-item v-for="lang in languages" :key="lang.culture" :value="lang.culture" :text="lang.culture" />
      </vs-select>
      <button
        @click="addNewApp"
        class="flex items-center gap-2 bg-blue-800 hover:bg-blue-900 text-white font-semibold px-3 py-2 rounded-lg shadow transition"
      >
        <span class="material-icons text-base">add</span>
        Add App
      </button>
    </div>
    <!-- table -->
    <div class="overflow-x-auto rounded-xl border border-gray-200 bg-white pb-2">
      <table class="min-w-full bg-white rounded-xl">
        <thead class="border-b">
          <tr>
            <th class="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">#</th>
            <th class="px-4 py-3">App Icon</th>
            <th class="px-4 py-3">Banner Image</th>
            <th class="px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-3/12">Title</th>
            <th class="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Bundle ID</th>
            <th class="px-4 py-3 text-xs text-center font-semibold text-gray-500 uppercase">App URL</th>
            <th class="px-4 py-3 text-xs text-center font-semibold text-gray-500 uppercase">Status</th>
            <th class="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody></tbody>
        <tbody>
          <tr v-for="(item, idx) in all" :key="item._id" class="border-b hover:bg-gray-100 transition">
            <td class="px-4 py-1 text-sm text-gray-800">{{ idx + 1 }}</td>
            <td class="px-4 py-1">
              <img v-if="item.iconURL" :src="item.iconURL" alt="App Icon" class="w-12 h-12 object-cover" />
              <span v-else class="text-gray-800">No Icon</span>
            </td>
            <td class="px-4 py-1">
              <img v-if="item.bannerURL" :src="item.bannerURL" alt="Banner Image" class="w-24 h-12 object-contain" />
              <span v-else class="text-gray-800">No Banner</span>
            </td>
            <td class="px-4 py-1 text-sm text-gray-800">
              <div class="font-medium">{{ item.title }} ({{ item.language }})</div>
              <div class="text-xs text-gray-500">{{ item.description }}</div>
            </td>
            <td class="px-4 py-1 text-sm text-gray-800">
              {{ item.bundleId }}
            </td>
            <td class="px-4 py-1 text-center">
              <span class="material-icons" style="cursor: pointer" @click="viewAppURL(item)">remove_red_eye</span>
            </td>
            <td class="px-4 py-1 text-center">
              <span
                :class="item.status ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'"
                class="inline-block px-3 py-1 rounded-full font-semibold"
              >
                {{ item.status ? "Active" : "Inactive" }}
              </span>
            </td>
            <td class="px-4 py-1">
              <vs-dropdown>
                <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="5" r="1.5" />
                  <circle cx="12" cy="12" r="1.5" />
                  <circle cx="12" cy="19" r="1.5" />
                </svg>
                <vs-dropdown-menu>
                  <vs-dropdown-item @click="editingItem(item)"> <vs-icon icon="create" class="align-right" /> Edit </vs-dropdown-item>
                  <vs-dropdown-item
                    @click="
                      deleteAppPopup = true;
                      itemToDelete = item;
                    "
                  >
                    <vs-icon icon="delete_sweep" /> Delete
                  </vs-dropdown-item>
                  <vs-dropdown-item
                    @click="
                      promoteAppPopup = true;
                      itemToPromote = item;
                    "
                  >
                    <vs-icon icon="star" /> Promote
                  </vs-dropdown-item>
                </vs-dropdown-menu>
              </vs-dropdown>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Code -->
    <vs-popup :title="isNew ? 'Add New App' : 'Edit App'" :active.sync="defaultalert">
      <div class="flex flex-col gap-2 px-2">
        <!-- language filter -->
        <div class="w-1/4" v-if="isNew">
          <label class="block text-sm font-semibold mb-2">Language</label>
          <vs-select v-model="app.language" class="w-full" :color="'#6366f1'" :disabled="showLoader">
            <vs-select-item value="" text="Select Language" disabled />
            <vs-select-item v-for="lang in languages" :key="lang.culture" :value="lang.culture" :text="lang.culture" />
          </vs-select>
        </div>
        <!-- title -->
        <label class="block text-sm font-semibold mb-2">Title</label>
        <vs-input v-model="app.title" placeholder="Keep short and simple" class="w-full" required :disabled="showLoader" />
        <!-- description -->
        <label class="block text-sm font-semibold mb-2">Description</label>
        <vs-textarea v-model="app.description" placeholder="App Description" class="w-full" required :disabled="showLoader" />
        <!-- bundle -->
        <label class="block text-sm font-semibold mb-2">Bundle ID</label>
        <vs-input v-model="app.bundleId" placeholder="Bundle Id" class="w-full" required :disabled="showLoader" />
        <!-- app url -->
        <label class="block text-sm font-semibold mb-2">App URL</label>
        <vs-input v-model="app.appURL" placeholder="App URL" class="w-full" required :disabled="showLoader" />
        <!-- status -->
        <vs-checkbox v-model="app.status" class="mb-2">Status (Active / Inactive)</vs-checkbox>
        <!-- images -->
        <div class="mb-6 flex flex-col md:flex-row md:items-center md:gap-6">
          <div class="flex-1" style="width: 100px; height: 150px">
            <div class="flex justify-between items-start">
              <!-- image -->
              <label class="block text-sm font-semibold">App Icon</label>
              <button
                v-if="displayImage"
                type="button"
                color="danger"
                class="bg-red-500 p-1 h-6 rounded-md hover:bg-red-700 transition text-white"
                @click="clearImage"
                :disabled="showLoader"
              >
                Clear
              </button>
            </div>
            <!-- image -->
            <div
              class="flex flex-col items-center justify-center border-2 border-dashed rounded-md cursor-pointer bg-gray-50 hover:bg-gray-100 transition"
              style="width: 100%; height: 140px; min-height: 140px"
              @dragover.prevent
              @dragenter.prevent
              @drop.prevent="onDropImage"
              @click="triggerFileInput"
            >
              <img v-if="displayImage" :src="displayImage" class="rounded-md object-cover border" style="width: 200px; max-height: 140px" />
              <div v-else class="flex flex-col items-center justify-center w-full h-full text-gray-400">
                <span class="material-icons" style="font-size: 32px">cloud_upload</span>
                <span>Drag & Drop or Click to Upload</span>
              </div>
              <input
                type="file"
                ref="fileInput"
                class="hidden"
                @change="onImageChange"
                accept="image/jpeg,image/jpg,image/webp"
                :disabled="showLoader"
              />
            </div>
          </div>
          <!-- banner upload -->
          <div class="flex-1" style="width: 100px; height: 150px">
            <div class="flex justify-between items-center">
              <label class="block text-sm font-semibold">Banner Image</label>
              <button
                v-if="displayBannerImage"
                type="button"
                color="danger"
                class="bg-red-500 p-1 h-6 rounded-md hover:bg-red-700 transition text-white"
                @click="clearBannerImage"
                :disabled="showLoader"
              >
                Clear
              </button>
            </div>
            <div
              class="flex flex-col items-center justify-center border-2 border-dashed rounded-md cursor-pointer bg-gray-50 hover:bg-gray-100 transition"
              style="width: 100%; height: 140px; min-height: 140px"
              @dragover.prevent
              @dragenter.prevent
              @drop.prevent="onDropImageBanner"
              @click="triggerBannerFileInput"
            >
              <img
                v-if="displayBannerImage"
                :src="displayBannerImage"
                class="rounded-md object-cover border"
                style="width: 200px; max-height: 140px"
              />
              <div v-else class="flex flex-col items-center justify-center w-full h-full text-gray-400">
                <span class="material-icons" style="font-size: 32px">cloud_upload</span>
                <span>Drag & Drop or Click to Upload</span>
              </div>
              <input
                type="file"
                ref="bannerFileInput"
                class="hidden"
                @change="onBannerImageChange"
                accept="image/jpeg,image/jpg,image/webp"
                :disabled="showLoader"
              />
            </div>
          </div>
        </div>
        <!-- save button -->
        <vs-button class="flex-1" @click="btnAddApp" icon="save">Save App</vs-button>
      </div>
    </vs-popup>

    <vs-popup title="Delete App" :active.sync="deleteAppPopup">
      <p class="mb-4 text-lg text-gray-700">Are you sure you want to delete this App?</p>
      <div class="flex justify-end gap-2">
        <vs-button color="warning" @click="deleteApp">Delete</vs-button>
        <vs-button color="primary" @click="deleteAppPopup = false">Cancel</vs-button>
      </div>
    </vs-popup>

    <vs-popup v-if="promoteAppPopup" :title="popupTitle + ' for ' + itemToPromote.title" :active.sync="promoteAppPopup">
      <vs-button
        icon="create"
        color="primary"
        type="border"
        id="tab-button-insert"
        class="app-button"
        @click="selectedComponent = 'insertPromote'"
        :class="selectedComponent == 'insertPromote' ? 'colored' : ''"
        popupTitle="Insert Promotion Apps"
        >Insert Promotion App</vs-button
      >
      <vs-button
        color="primary"
        icon="view_list"
        id="tab-button-view"
        type="border"
        @click="
          selectedComponent = 'viewPromote';
          popupTitle = 'View Promoted Apps';
        "
        :class="selectedComponent == 'viewPromote' ? 'colored' : ''"
        >View Promoted Apps</vs-button
      >
      <component
        @promoted-object="promoteObject"
        :editObject="editObject"
        :is="selectedComponent"
        :key="componentKey"
        :itemToPromote="itemToPromote"
        :languageSelect="language"
        @delete-promoted-item="deletePromotedAppProp"
      />
    </vs-popup>

    <vs-popup class="holamundo" title="Delete Promoted App" :active.sync="deletePromotedAppPopup">
      <p>Are you sure you want to delete Promoted App?</p>
      <div>
        <vs-button icon="delete_forever" class="mr15" @click="deletePromotedApp">Delete Promoted App</vs-button>
        <vs-button icon="clear" @click="deletePromotedAppPopup = false">Cancel</vs-button>
      </div>
    </vs-popup>

    <vs-popup title="View APP URL" :active.sync="viewAppURLPopup">
      <div v-html="appURL"></div>
    </vs-popup>
  </div>
</template>

<script>
import { API } from "@/common/constants";
import api from "@/rest.js";
import insertPromote from "../../components/insertPromotedApps.vue";
import viewPromote from "../../components/viewPromotedApps.vue";

export default {
  name: "App",
  components: {
    insertPromote,
    viewPromote,
  },
  data: () => ({
    title: "App",
    colorx: "#0D2144",
    popupTitle: "Insert Promotion App",
    defaultalert: false,
    deleteAppPopup: false,
    promoteAppPopup: false,
    deletePromotedAppPopup: false,
    componentKey: 0,
    selectedComponent: "insertPromote",
    seeReviews: false,
    isNew: true,
    itemToDelete: "",
    itemToUpdate: "",
    itemToPromote: {},
    appURL: "",
    viewAppURLPopup: false,
    promotedAppDeleteItem: "",
    app: {
      title: "",
      description: "",
      iconURL: "",
      bannerURL: "",
      bundleId: "",
      status: true,
      appURL: "",
      language: "",
    },
    all: [],
    language: "EN",
    languages: [],
    editObject: null,
    displayImage: "",
    fileName: "",
    displayBannerImage: "",
    fileNameBanner: "",
    showLoader: false,
  }),
  methods: {
    addNewApp() {
      this.$data.defaultalert = true;
      this.$data.isNew = true;
      (this.$data.app = {
        title: "",
        description: "",
        iconURL: "",
        bannerURL: "",
        bundleId: "",
        status: true,
        appURL: "",
        language: "",
      }),
        (this.$data.displayImage = "");
      this.$data.displayBannerImage = "";
    },

    editingItem(i) {
      this.$data.app = Object.assign({}, i);
      this.$data.defaultalert = true;
      this.$data.isNew = false;
      this.$data.displayImage = i.iconURL;
      this.$data.displayBannerImage = i.bannerURL;
    },

    promoteObject(val) {
      this.selectedComponent = "insertPromote";
      this.editObject = Object.assign({}, val);
    },

    deletePromotedAppProp(val) {
      this.$data.promotedAppDeleteItem = val;
      this.$data.deletePromotedAppPopup = true;
    },

    viewAppURL(i) {
      this.$data.viewAppURLPopup = true;
      this.$data.appURL = i.appURL;
    },

    onImageChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.app.iconURL = file;
        this.displayImage = URL.createObjectURL(file);
      }
    },

    onBannerImageChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.app.bannerURL = file;
        this.displayBannerImage = URL.createObjectURL(file);
      } else {
        this.displayBannerImage = "";
      }
    },

    clearImage() {
      this.displayImage = "";
      this.app.iconURL = "";
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    clearBannerImage() {
      this.displayBannerImage = "";
      this.app.bannerURL = "";
      if (this.$refs.bannerFileInput) {
        this.$refs.bannerFileInput.value = "";
      }
    },

    triggerFileInput() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },

    triggerBannerFileInput() {
      if (this.$refs.bannerFileInput) this.$refs.bannerFileInput.click();
    },

    onDropImage(e) {
      const files = e.dataTransfer.files;
      if (files && files[0]) {
        this.onImageChange({ target: { files } });
      }
    },

    onDropImageBanner(e) {
      const files = e.dataTransfer.files;
      if (files && files[0]) {
        this.onBannerImageChange({ target: { files } });
      }
    },

    async uploadImage(data, item) {
      const imageType = ["image/jpeg", "image/jpg", "image/webp"];
      const file = data.target.files[0];

      if (!file || !imageType.includes(file.type)) {
        this.$vs.notify({
          title: "Uploading Error",
          text: "Only JPEG, JPG, WEBP Image files formats should be supported",
          color: "danger",
          position: "top-right",
        });
        throw new Error("Invalid file type");
      }

      if (file.size > 500000) {
        this.$vs.notify({
          title: "Uploading Error",
          text: "File size should be less than 500kb",
          color: "danger",
          position: "top-right",
        });
        throw new Error("File size exceeds limit");
      }

      const formdata = new FormData();
      formdata.append("file", file);
      formdata.append("app", this.$data.app.name);

      if (item === "appURL") {
        this.$data.fileName = file.name;
      } else {
        this.$data.fileNameBanner = file.name;
      }

      this.$vs.loading();
      try {
        const response = await api.post(API.UPLOAD_IMAGE, formdata, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        const uploadedUrl = response.data.files[0].url;
        if (uploadedUrl) {
          if (item === "appURL") this.$data.displayImage = uploadedUrl;
          else this.$data.displayBannerImage = uploadedUrl;

          this.$vs.notify({
            text: "File Uploaded",
            color: "success",
            position: "top-right",
          });
        }
      } catch (error) {
        this.$vs.notify({
          title: "Error",
          text: "An error occurred during file upload.",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    async btnAddApp() {
      this.$vs.loading();

      try {
        let apiToCall = API.INSERT_APP;
        let data = this.$data.app;

        if (this.$data.app.iconURL instanceof File) await this.uploadImage({ target: { files: [this.$data.app.iconURL] } }, "appURL");
        if (this.$data.app.bannerURL instanceof File) await this.uploadImage({ target: { files: [this.$data.app.bannerURL] } }, "bannerURL");

        this.$data.app.iconURL = this.$data.displayImage;
        this.$data.app.bannerURL = this.$data.displayBannerImage;
        const requiredFields = [
          { key: "title", label: "App Title" },
          { key: "bundleId", label: "App BundleId" },
          { key: "appURL", label: "App URL" },
          { key: "bannerURL", label: "App Banner URL" },
          { key: "iconURL", label: "App Icon URL" },
        ];

        for (const field of requiredFields) {
          if (!this.$data.app[field.key]) {
            this.$vs.notify({
              title: "Error",
              text: `${field.label} Can't be Empty`,
              color: "danger",
              position: "top-right",
            });
            return;
          }
        }

        if (!this.$data.isNew) {
          apiToCall = API.UPDATE_APP;
          data["iconURL"] = this.$data.displayImage;
          data["bannerURL"] = this.$data.displayBannerImage;

          const r = await api.put(`${apiToCall}/${this.$data.app._id}`, data);

          if (r.status == 200)
            this.$vs.notify({
              title: "Success",
              text: "Updated Successfully",
              color: "success",
              position: "top-right",
            });
        } else {
          const r = await api.post(apiToCall, data);
          if (r.status == 200)
            this.$vs.notify({
              title: "Success",
              text: "Saved Successfully",
              color: "success",
              position: "top-right",
            });
        }

        this.$data.isNew = true;
        this.$data.itemToUpdate = "";
        this.$data.defaultalert = false;
        this.$data.app = {
          language: "",
        };
        await this.fetchAllApps(this.$data.language);
      } catch (error) {
        this.$vs.notify({
          title: "Error",
          text: "An error occurred while saving the app.",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    async deleteApp() {
      this.$vs.loading();
      try {
        const r = await api.delete(`${API.DELETE_APP}/${this.$data.itemToDelete._id}`);
        if (r.status) {
          this.$vs.notify({
            text: "Deleted Successfully",
            color: "success",
            position: "top-right",
          });
          this.$data.deleteAppPopup = false;
          await this.fetchAllApps(this.$data.language);
        }
      } catch (error) {
        this.$vs.notify({
          text: "Error deleting app",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    async fetchAllApps(lang) {
      this.$vs.loading();
      try {
        const r = await api.get(`${API.GET_APP}?language=${lang}`);
        if (r.status === 200) this.$data.all = r.data;
      } catch (error) {
        this.$vs.notify({
          title: "Error",
          text: "Failed to fetch apps.",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    async deletePromotedApp() {
      this.$vs.loading();
      try {
        const r = await api.delete(`${API.DELETE_PROMOTE_APP}/${this.$data.promotedAppDeleteItem._id}`);
        if (r.status === 200) {
          this.$vs.notify({
            text: "Deleted Successfully",
            color: "success",
            position: "top-right",
          });
          this.$data.deletePromotedAppPopup = false;
          this.$data.componentKey++;
        }
      } catch (error) {
        this.$vs.notify({
          text: "Error deleting promoted app",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    async fetchAllLanguages() {
      this.$vs.loading();
      try {
        const r = await api.get(API.GET_LANG, {});
        this.languages = r.data;
      } catch (error) {
        this.$vs.notify({
          text: "Error fetching languages",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
      }
    },
  },

  async created() {
    this.language = "all";

    await Promise.all([this.fetchAllLanguages(), this.fetchAllApps(this.language)]);
  },
};
</script>

<style scoped>
.material-icons {
  font-family: "Material Icons";
  font-style: normal;
  font-weight: normal;
  font-size: 20px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  direction: ltr;
  -webkit-font-smoothing: antialiased;
}
</style>
